import { useEffect, useId, useRef, useState } from 'react';
import { personLabel, searchPeople } from '../lib/people.js';

/**
 * Type-ahead over the IHS directory.
 *
 * Design rules, all of them load-bearing:
 *
 *  - **Typing always wins.** If nothing is picked, whatever was typed is the
 *    value. Contractors and brand-new accounts are not in Entra, and a picker
 *    that refuses to submit is worse than the free-text box it replaced.
 *  - **Silence when the directory is down.** No error banner, no red text — the
 *    dropdown simply never opens and it behaves as a plain input.
 *  - **Name and email, nothing else.** Department is set on 1 of 844 IHS
 *    accounts and job title on 90, so email is the only reliable way to tell two
 *    similar names apart. Job title shows only when it happens to be there.
 */

const DEBOUNCE_MS = 250;
const MIN_QUERY = 2;

export default function PersonPicker({ value, onChange, invalid, placeholder = 'Start typing a name…', label, onEnter }) {
  const [text, setText] = useState(() => personLabel(value));
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const listId = useId();

  // A value set from outside (the manager auto-fill) must show up in the box.
  // Only sync when it genuinely differs, so it can't fight the user's typing.
  useEffect(() => {
    const shown = personLabel(value);
    setText((current) => (shown && shown !== current ? shown : current));
  }, [value]);

  // Debounced search. The abort controller matters: without it a slow early
  // keystroke can land after a fast later one and repopulate a stale list.
  useEffect(() => {
    const q = text.trim();
    // Don't re-search the name we just resolved — but ONLY when it came from the
    // directory. Plain typing also writes the text straight to `value`, so
    // comparing against every value (rather than picked ones) would match on
    // every keystroke and the dropdown would never open.
    const settled = value && typeof value === 'object' && q === personLabel(value);
    if (q.length < MIN_QUERY || settled) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(async () => {
      const { people } = await searchPeople(q, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setResults(people);
      setActive(-1);
      setLoading(false);
      if (people.length) setOpen(true);
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [text, value]);

  // Close when focus leaves the whole control, not just the input — otherwise
  // clicking a result closes the list before the click registers.
  useEffect(() => {
    function onDocDown(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  function choose(p) {
    setText(personLabel(p));
    setOpen(false);
    setResults([]);
    onChange(p);
  }

  function onKeyDown(e) {
    // Enter with no highlighted result is the caller's (the desk box adds a typed address).
    if (e.key === 'Enter' && onEnter && !(open && results.length && active >= 0)) {
      e.preventDefault();
      onEnter();
      return;
    }
    if (!open || !results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      choose(results[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const picked = value && typeof value === 'object';

  return (
    <div className="pp" ref={boxRef}>
      <input
        className="input"
        style={invalid ? { outline: '2px solid var(--ihs-red)' } : undefined}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        placeholder={placeholder}
        aria-label={label}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          // Typing after picking someone drops back to plain text, so the
          // stored person can never disagree with what is on screen.
          onChange(e.target.value);
        }}
        onFocus={() => results.length && setOpen(true)}
        onKeyDown={onKeyDown}
      />

      {picked ? (
        <span className="pp__picked">
          <span className="material-symbols-outlined" aria-hidden="true">check_circle</span>
          {value.mail || 'from the directory'}
          {value.jobTitle ? ` · ${value.jobTitle}` : ''}
        </span>
      ) : null}

      {open && results.length ? (
        <ul className="pp__list" id={listId} role="listbox">
          {results.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === active}>
              <button
                type="button"
                className={`pp__opt${i === active ? ' pp__opt--on' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(p)}
              >
                <b>{p.displayName}</b>
                <span>{p.mail}{p.jobTitle ? ` · ${p.jobTitle}` : ''}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {loading ? <span className="pp__hint">Searching the directory…</span> : null}
    </div>
  );
}
